import { useFormatting } from "@msys/ui";
import { clamp } from "lodash-es";
import React from "react";
import { FormattedField } from "./FormattedField.js";

export const FormattedFloatOptionalField = ({
  name,
  unit,
  min,
  max,
  step,
  multiplier = 1,
  setFieldValue,
  prepareActualValue,
  ...props
}: {
  name: string;
  unit?: string;
  min?: number;
  max?: number;
  step?: number;
  multiplier?: number;
  setFieldValue?: (field: string, value: number | null) => void;
  prepareActualValue?: (value: number) => number;
} & Omit<
  React.ComponentProps<typeof FormattedField<number>>,
  "getFormattedValue" | "getActualValue" | "getEditableValue"
>) => {
  const { getFormattedFloat, getEditableFloat, getFloat } = useFormatting();

  function getFormattedValue(value: number | null | undefined) {
    if (value === null || value === undefined) return undefined;
    return getFormattedUnit(
      getFormattedFloat((value ?? 0) * multiplier, step),
      unit
    );
  }

  function getActualValue(value: string) {
    let floatValue: number | null =
      value === "" ? null : getFloat(value, false);

    if (Number.isNaN(floatValue) || floatValue === null) return null;

    floatValue = floatValue / multiplier;
    if (step) floatValue = Math.round(floatValue / step) * step;
    const newValue = prepareActualValue
      ? prepareActualValue(
          clamp(floatValue, Number.MIN_SAFE_INTEGER, Number.MAX_SAFE_INTEGER)
        )
      : clamp(
          floatValue,
          min ?? Number.MIN_SAFE_INTEGER,
          max ?? Number.MAX_SAFE_INTEGER
        );
    return newValue;
  }

  function getEditableValue(value: number | null | undefined) {
    if (value === null || value === undefined) return "";
    return getEditableFloat((value ?? 0) * multiplier, false, step);
  }

  return (
    <FormattedField<number | null>
      {...props}
      name={name}
      getFormattedValue={getFormattedValue}
      getActualValue={getActualValue}
      getEditableValue={getEditableValue}
      setFieldValue={setFieldValue}
    />
  );
};

function getFormattedUnit(value: string, unit?: string) {
  return `${value}${unit ?? ""}`.trim();
}
